import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import Colours from '@shared/ui/atoms/Colours';
import { HeadlineLarge } from '@shared/ui/atoms/fonts/Headline';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';
import { KeypadInput } from '../../kiosk/components/KeypadInput';
import {
  cancelSignagePairing,
  clearSignagePairingError,
  pollSignagePairingStatus,
  selectSignage,
  submitSignagePairingCode,
} from '../data/signage.duck';

const CODE_LENGTH = 8;

export function SignagePairingScreen() {
  const dispatch = useDispatch();
  const signage = useSelector(selectSignage);
  const [code, setCode] = useState('');

  const submit = (full: string) => {
    if (signage.pairing || signage.pairingStatus === 'pending') return;
    dispatch(submitSignagePairingCode({ code: full }));
  };

  const handleChange = (next: string) => {
    if (signage.pairingError) dispatch(clearSignagePairingError());
    setCode(next);
  };

  const resetToKeypad = () => {
    setCode('');
    dispatch(cancelSignagePairing());
  };

  useEffect(() => {
    if (signage.pairingStatus !== 'pending' || !signage.pendingRequestId) return;
    const requestId = signage.pendingRequestId;
    const interval = window.setInterval(() => {
      dispatch(pollSignagePairingStatus({ requestId }));
    }, Math.max(500, signage.pollIntervalMs));
    return () => window.clearInterval(interval);
  }, [dispatch, signage.pairingStatus, signage.pendingRequestId, signage.pollIntervalMs]);

  return (
    <Container>
      {signage.pairingStatus === 'pending' ? (
        <>
          <Spinner />
          <HeadlineLarge style={{ marginBottom: 12, textAlign: 'center' }}>
            Waiting for approval…
          </HeadlineLarge>
          <BodyRegular style={{ marginBottom: 8, textAlign: 'center', color: Colours.darkGrey }}>
            Ask your admin to approve this screen in company settings &gt; Other &gt; Office signage.
          </BodyRegular>
          <CancelLink onClick={resetToKeypad}>Cancel</CancelLink>
        </>
      ) : signage.pairingStatus === 'rejected' ? (
        <ResultPanel
          title="Connection rejected"
          message="Your admin declined the pairing request. Ask them to try again."
          onTryAgain={resetToKeypad}
        />
      ) : signage.pairingStatus === 'expired' ? (
        <ResultPanel
          title="Request expired"
          message="No one approved the pairing request in time. Try again."
          onTryAgain={resetToKeypad}
        />
      ) : (
        <>
          <HeadlineLarge style={{ marginBottom: 12, textAlign: 'center' }}>
            Pair this screen
          </HeadlineLarge>
          <BodyRegular style={{ marginBottom: 24, textAlign: 'center', color: Colours.darkGrey }}>
            Enter the 8-digit signage code from your admin
          </BodyRegular>
          <KeypadInput
            value={code}
            maxLength={CODE_LENGTH}
            onChange={handleChange}
            onComplete={submit}
            disabled={signage.pairing}
          />
          {signage.pairingError && (
            <ErrorMessage>{signage.pairingError}</ErrorMessage>
          )}
          {signage.pairing && (
            <BodyRegular style={{ marginTop: 16, color: Colours.darkGrey }}>Submitting…</BodyRegular>
          )}
        </>
      )}
    </Container>
  );
}

function ResultPanel({ title, message, onTryAgain }: { title: string; message: string; onTryAgain: () => void }) {
  return (
    <>
      <HeadlineLarge style={{ marginBottom: 12, textAlign: 'center' }}>{title}</HeadlineLarge>
      <BodyRegular style={{ marginBottom: 24, textAlign: 'center', color: Colours.darkGrey }}>
        {message}
      </BodyRegular>
      <PrimaryButton text={'Try again'} size={ButtonSize.Large} click={onTryAgain} />
    </>
  );
}

// Shared card treatment: radius 20 + the ContentCard blue-glow shadow. The
// brand logo lives in the signage top bar, not in the card.
const Container = styled.div`
  width: 100%;
  max-width: 520px;
  margin: 0 auto;
  padding: 32px;
  background: white;
  border-radius: 20px;
  box-shadow: 0 12px 32px 2px rgba(0, 87, 255, 0.15);
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ErrorMessage = styled.div`
  margin-top: 16px;
  padding: 12px 16px;
  background: white;
  border: 1px solid ${Colours.red};
  color: ${Colours.red};
  border-radius: 8px;
  font-weight: 500;
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  margin-bottom: 16px;
  border-radius: 50%;
  border: 4px solid ${Colours.lightGrey};
  border-top-color: ${Colours.blue};
  animation: signage-spin 1s linear infinite;
  @keyframes signage-spin { to { transform: rotate(360deg); } }
`;

const CancelLink = styled.button`
  margin-top: 24px;
  background: none;
  border: none;
  color: ${Colours.darkGrey};
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;
`;

